import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, Bot, LogOut, Sparkles } from 'lucide-react';
import { useFirebase } from '../context/FirebaseContext';
import styled from 'styled-components';

const TopBar = () => {
  const { user, logout } = useFirebase();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [currentAnnouncement, setCurrentAnnouncement] = useState(0);
  const dropdownRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isHidden, setIsHidden] = useState(window.scrollY >= 10);
  const [topBarHeight, setTopBarHeight] = useState(window.innerWidth <= 1024 ? 35 : 45);
  const prevScrollY = useRef(window.scrollY);

  // Handle window resize and set top bar height
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setTopBarHeight(window.innerWidth <= 1024 ? 35 : 45);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Scroll handler to control visibility
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const direction = currentScrollY > prevScrollY.current ? 'down' : 'up';
      if (direction === 'down') {
        setIsHidden(true);
      } else if (direction === 'up') {
        setIsHidden(currentScrollY >= 10);
      }
      prevScrollY.current = currentScrollY;
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Set CSS variable for navbar positioning
  useEffect(() => {
    const height = isHidden ? '0px' : `${topBarHeight}px`;
    document.documentElement.style.setProperty('--top-bar-height', height);
  }, [isHidden, topBarHeight]);

  // Announcements arrays
  const desktopAnnouncements = [
    { text: "Build Your Own AI Chatbot in Minutes", color: '#3b82f6', url: "/vuxo-chat", image: 'https://i.imgur.com/V2nyzko.gif' },
    { text: "AI for Health Starts June 9, 7 PM, call support for details.", color: '#a855f7', url: "/workshops", image: 'https://i.imgur.com/V2nyzko.gif' },
    { text: "AI Voice Agent for Seamless, 24/7 Call Handling", color: '#ec4899', url: "/vuxo-voice", image: 'https://i.imgur.com/V2nyzko.gif' },
  ];

  const mobileAnnouncements = [
    { text: "Build AI Chatbot", color: '#3b82f6', url: "/vuxo-chat" },
    { text: "AI For health, June 9th 7PM", color: '#a855f7', url: "/workshops" },
    { text: "24/7 Call Support", color: '#ec4899', url: "/about" },
  ];

  const announcements = isMobile ? mobileAnnouncements : desktopAnnouncements;

  // Cycle announcements
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentAnnouncement(prev => (prev + 1) % announcements.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [announcements.length]);

  // Close dropdown on click outside with debounce
  useEffect(() => {
    let timeoutId;
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        timeoutId = setTimeout(() => {
          setDropdownOpen(false);
        }, 100);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      clearTimeout(timeoutId);
    };
  }, []);

  // Close dropdown if TopBar is hidden
  useEffect(() => {
    if (isHidden) setDropdownOpen(false);
  }, [isHidden]);

  const handleLogout = async () => {
    try {
      await logout();
      sessionStorage.removeItem('sessionId');
      sessionStorage.removeItem('activeDocId');
      setDropdownOpen(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Determine display text
  const displayText = user ? (isMobile ? 'Welcome' : `Welcome ${user.displayName || user.email}`) : '';

  return (
    <motion.div
      className="top-bar"
      initial={{ y: 0 }}
      animate={{ y: isHidden ? -topBarHeight : 0 }}
      transition={{ type: 'spring', stiffness: 100, damping: 25 }}
    >
      <div className="top-bar-container">
        {/* Demo Section */}
        <div className="demo-section">
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} transition={{ type: 'spring', stiffness: 200, damping: 15 }}>
            <Link to="/wizard" className="demo-button-link">
              <StyledButton>
                <div className="button-container">
                  <Sparkles size={isMobile ? 12 : 14} className="button-icon" />
                  <span className="button-text">{isMobile ? 'Build My Bot' : 'Create My Bot'}</span>
                </div>
              </StyledButton>
            </Link>
          </motion.div>
        </div>

        {/* Announcement Carousel */}
        <div className="announcement-wrapper">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentAnnouncement}
              className="announcement-content"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ type: 'spring', stiffness: 150, damping: 20 }}
              whileHover={{ scale: 1.05 }}
            >
              {announcements[currentAnnouncement].url ? (
                <Link to={announcements[currentAnnouncement].url} className="announcement-link">
                  {!isMobile && (
                    <img
                      src={announcements[currentAnnouncement].image}
                      alt="Announcement Icon"
                      className="announcement-image"
                    />
                  )}
                  <span className="announcement-text">
                    {announcements[currentAnnouncement].text}
                  </span>
                </Link>
              ) : (
                <div className="announcement-link">
                  {!isMobile && (
                    <img
                      src={announcements[currentAnnouncement].image}
                      alt="Announcement Icon"
                      className="announcement-image"
                    />
                  )}
                  <span className="announcement-text">
                    {announcements[currentAnnouncement].text}
                  </span>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
          <motion.div
            className="progress-bar"
            style={{ background: announcements[currentAnnouncement].color }}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 5, ease: 'linear' }}
            key={currentAnnouncement}
          />
        </div>

        {/* Profile/Login Section */}
        {user ? (
          <div className="profile-container" ref={dropdownRef}>
            <motion.div
              className="profile-trigger"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15 }}
            >
              <Link to="/dashboard/bots">
                <img
                  src="https://i.imgur.com/sdCwVCY.gif"
                  alt="Profile"
                  className="profile-picture"
                />
              </Link>
              <span className="profile-text" onClick={() => setDropdownOpen(!dropdownOpen)}>
                {displayText}
                {dropdownOpen ? (
                  <ChevronUp className="dropdown-arrow" size={14} />
                ) : (
                  <ChevronDown className="dropdown-arrow" size={14} />
                )}
              </span>
            </motion.div>
            <AnimatePresence>
              {dropdownOpen && (
                <motion.div
                  className="dropdown"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ type: 'spring', stiffness: 180, damping: 20 }}
                >
                  <ul>
                    <motion.li
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                    >
                      <Link to="/dashboard/bots">
                        <Bot size={16} className="dropdown-icon" />
                        My Chatbots
                      </Link>
                    </motion.li>
                    <motion.li
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                      onClick={handleLogout}
                    >
                      <LogOut size={16} className="dropdown-icon" />
                      Logout
                    </motion.li>
                  </ul>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ) : (
          <Link to="/login" className="profile-container-link">
            <motion.div
              className="profile-container"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15 }}
            >
              <div className="profile-trigger">
                <img
                  src="https://i.imgur.com/sdCwVCY.gif"
                  alt="Profile"
                  className="profile-picture"
                />
                <span className="profile-text">Login</span>
              </div>
            </motion.div>
          </Link>
        )}
      </div>
    </motion.div>
  );
};

// Styled Button Component
const StyledButton = styled.div`
  .button-container {
    --background: #fefbff;
    --inner-shadow: #9faef9;
    --inner-outline: #34217d;
    --inner-outline-bottom: #fafcfe;
    --inner-outline-middle: #8880d5;
    --inner-outline-outer-top: #fefcff;
    --inner-outline-outer-bottom-1: #29107b;
    --text-color: #34217d;
    position: relative;
    background: var(--background);
    cursor: pointer;
    font-size: 0.85rem;
    font-weight: 600;
    padding: 6px 14px;
    border-radius: 5px;
    box-shadow: inset 0px 1px 4px 0px var(--inner-shadow),
      0px 1px 0px 0px var(--inner-outline-bottom),
      0px -1px 0px 0px var(--inner-outline),
      -1px -1px 0px 0px var(--inner-outline),
      1px -1px 0px 0px var(--inner-outline),
      0px 0px 0px 3px var(--inner-outline-middle),
      0px -1px 0px 4px var(--inner-outline-outer-top),
      0px 2px 0px 4px var(--inner-outline-outer-bottom-1);
    display: flex;
    align-items: center;
    gap: 6px;
    transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
    white-space: nowrap;
    height: 28px; /* Matches navbar height constraints */
  }

  .button-container:hover {
    background: #f5f2ff;
    box-shadow: inset 0px 1px 4px 0px var(--inner-shadow),
      0px 1.5px 0px 0px var(--inner-outline-bottom),
      0px -1.5px 0px 0px var(--inner-outline),
      -1.5px -1.5px 0px 0px var(--inner-outline),
      1.5px -1.5px 0px 0px var(--inner-outline),
      0px 0px 0px 3.5px var(--inner-outline-middle),
      0px -1.5px 0px 4.5px var(--inner-outline-outer-top),
      0px 2.5px 0px 4.5px var(--inner-outline-outer-bottom-1);
  }

  .button-container:active {
    transform: scale(0.98);
    box-shadow: inset 0px 1px 3px 0px var(--inner-shadow),
      0px 0.5px 0px 0px var(--inner-outline-bottom),
      0px -0.5px 0px 0px var(--inner-outline),
      -0.5px -0.5px 0px 0px var(--inner-outline),
      0.5px -0.5px 0px 0px var(--inner-outline),
      0px 0px 0px 2.5px var(--inner-outline-middle),
      0px -0.5px 0px 3.5px var(--inner-outline-outer-top),
      0px 1.5px 0px 3.5px var(--inner-outline-outer-bottom-1);
  }

  .button-icon {
    color: var(--text-color);
    width: 14px;
    height: 14px;
    transition: transform 0.2s ease;
  }

  .button-container:hover .button-icon {
    transform: rotate(15deg);
  }

  .button-text {
    color: var(--text-color);
    transition: text-shadow 0.2s ease;
  }

  .button-container:hover .button-text {
    text-shadow: 0 0 3px rgba(52, 33, 125, 0.3);
  }

  @media (max-width: 768px) {
    .button-container {
      padding: 4px 4px;
      font-size: 0.6rem;
      height: 20px;
      box-shadow: inset 0px 1px 1.5px 0px var(--inner-shadow),
        0px 0.5px 0px 0px var(--inner-outline-bottom),
        0px -0.5px 0px 0px var(--inner-outline),
        -0.5px -0.5px 0px 0px var(--inner-outline),
        0.5px -0.5px 0px 0px var(--inner-outline),
        0px 0px 0px 1px var(--inner-outline-middle),
        0px -0.5px 0px 1.5px var(--inner-outline-outer-top),
        0px 0.5px 0px 1.5px var(--inner-outline-outer-bottom-1);
    }

    .button-container:hover {
      background: #f5f2ff;
      box-shadow: inset 0px 1px 1.5px 0px var(--inner-shadow),
        0px 0.75px 0px 0px var(--inner-outline-bottom),
        0px -0.75px 0px 0px var(--inner-outline),
        -0.75px -0.75px 0px 0px var(--inner-outline),
        0.75px -0.75px 0px 0px var(--inner-outline),
        0px 0px 0px 1.25px var(--inner-outline-middle),
        0px -0.75px 0px 1.75px var(--inner-outline-outer-top),
        0px 0.75px 0px 1.75px var(--inner-outline-outer-bottom-1);
    }

    .button-container:active {
      transform: scale(0.98);
      box-shadow: inset 0px 1px 1px 0px var(--inner-shadow),
        0px 0.25px 0px 0px var(--inner-outline-bottom),
        0px -0.25px 0px 0px var(--inner-outline),
        -0.25px -0.25px 0px 0px var(--inner-outline),
        0.25px -0.25px 0px 0px var(--inner-outline),
        0px 0px 0px 0.75px var(--inner-outline-middle),
        0px -0.25px 0px 1.25px var(--inner-outline-outer-top),
        0px 0.5px 0px 1.25px var(--inner-outline-outer-bottom-1);
    }

    .button-icon {
      width: 12px;
      height: 12px;
    }

    .button-container:hover .button-icon {
      transform: rotate(15deg);
    }
  }
`;

// CSS Styles
const styles = `
  /* Top bar container styling */
  .top-bar {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 45px;
    background: rgba(30, 41, 59, 0.8);
    backdrop-filter: blur(10px);
    z-index: 1010;
    display: flex;
    justify-content: center;
    align-items: center;
    box-shadow: 0 8px 20px rgba(0,0,0,0.4);
    border-bottom-left-radius: 25px;
    border-bottom-right-radius: 25px;
    touch-action: manipulation;
    border: 1px solid transparent;
    background-image: linear-gradient(
      rgba(30, 41, 59, 0.8),
      rgba(30, 41, 59, 0.8)
    ),
    linear-gradient(
      45deg,
      #3b82f6,
      #a855f7,
      #ec4899
    );
    background-origin: border-box;
    background-clip: padding-box, border-box;
    animation: gradientBorder 5s linear infinite;
  }
  @keyframes gradientBorder {
    0% { background-position: 0% 0%; }
    100% { background-position: 400% 0%; }
  }
  .top-bar-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    max-width: 1200px;
    padding: 0 16px;
  }
  .profile-container-link, .demo-button-link {
    text-decoration: none;
  }
  /* Profile/Login Section */
  .profile-container {
    display: flex;
    align-items: center;
    position: relative;
    touch-action: none;
  }
  .profile-trigger {
    display: flex;
    align-items: center;
    gap: 6px;
    user-select: none;
  }
  .profile-picture {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    transition: all 0.3s ease;
  }
  .profile-picture:hover {
    transform: scale(1.1);
    box-shadow: 0 0 15px rgba(168,85,247,0.6);
  }
  .profile-text {
    display: flex;
    align-items: center;
    gap: 4px;
    color: #d1d5db;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
  }
  .profile-text:hover {
    color: #a855f7;
    text-shadow: 0 0 8px rgba(168,85,247,0.5);
  }
  .dropdown-arrow {
    color: #a855f7;
    transition: all 0.3s ease;
  }
  .profile-trigger:hover .dropdown-arrow {
    color: #ffffff;
  }
  /* Dropdown Menu */
  .dropdown {
    position: absolute;
    top: calc(100% + 5px);
    right: 8px;
    width: 200px;
    background: rgba(30, 26, 71, 0.8);
    backdrop-filter: blur(10px);
    border-radius: 12px;
    padding: 15px 0;
    box-shadow: 0 8px 20px rgba(0,0,0,0.4);
    z-index: 1012;
    overflow: visible;
    border: 1px solid transparent;
    background-image: linear-gradient(
      rgba(30, 26, 71, 0.8),
      rgba(30, 26, 71, 0.8)
    ),
    linear-gradient(
      45deg,
      #3b82f6,
      #a855f7,
      #ec4899
    );
    background-origin: border-box;
    background-clip: padding-box, border-box;
    animation: gradientBorder 5s linear infinite;
  }
  .dropdown ul {
    list-style-type: none;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 0 10px;
    margin: 0;
  }
  .dropdown li {
    display: flex;
    align-items: center;
    color: #d1d5db;
    font-size: 0.8rem;
    font-weight: 600;
    padding: 8px 14px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease-out;
    touch-action: manipulation;
    gap: 8px;
  }
  .dropdown li:hover {
    background-color: #a855f7;
    color: #ffffff;
    transform: translate(1px, -1px);
    box-shadow: 0 0 15px rgba(168,85,247,0.5);
  }
  .dropdown li:active {
    transform: scale(0.98);
  }
  .dropdown li:last-child:hover {
    background-color: #8e2a2a;
    box-shadow: 0 0 15px rgba(142,42,42,0.5);
  }
  .dropdown li a {
    color: inherit;
    text-decoration: none;
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
  }
  .dropdown-icon {
    width: 16px;
    height: 16px;
    color: #d1d5db;
    transition: color 0.3s ease-out;
  }
  .dropdown li:hover .dropdown-icon {
    color: #ffffff;
  }
  /* Announcement carousel wrapper */
  .announcement-wrapper {
    flex: 1;
    max-width: 400px;
    height: 100%;
    overflow: hidden;
    position: relative;
    background: rgba(30, 41, 59, 0.6);
    backdrop-filter: blur(8px);
    border-bottom-left-radius: 25px;
    border-bottom-right-radius: 25px;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid transparent;
    background-image: linear-gradient(
      rgba(30, 41, 59, 0.6),
      rgba(30, 41, 59, 0.6)
    ),
    linear-gradient(
      45deg,
      #3b82f6,
      #a855f7,
      #ec4899
    );
    background-origin: border-box;
    background-clip: padding-box, border-box;
    animation: gradientBorder 5s linear infinite;
  }
  .announcement-content {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    padding: 0 8px;
    height: 100%;
    text-align: center;
    border-radius: 12px;
    transition: all 0.3s ease;
  }
  .announcement-content:hover {
    background: rgba(255,255,255,0.1);
    box-shadow: 0 0 15px rgba(59,130,246,0.5);
  }
  .announcement-link {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    text-decoration: none;
    width: 100%;
    text-align: center;
  }
  .announcement-image {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    transition: all 0.3s ease;
  }
  .announcement-content:hover .announcement-image {
    transform: scale(1.1);
  }
  .announcement-text {
    color: #ffffff;
    font-size: 0.85rem;
    font-weight: 600;
    text-shadow: 0 1px 2px rgba(0,0,0,0.5);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
    transition: all 0.3s ease;
  }
  .announcement-content:hover .announcement-text {
    text-shadow: 0 0 8px rgba(255,255,255,0.5);
  }
  .progress-bar {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 3px;
    transform-origin: left;
    box-shadow: 0 0 10px currentColor;
    transition: box-shadow 0.3s ease;
  }
  /* Demo Section */
  .demo-section {
    display: flex;
    align-items: center;
    z-index: 1012; /* Increased to ensure button is above announcement */
  }
  /* Responsive adjustments for tablet/mobile */
  @media (max-width: 1024px) {
    .top-bar {
      height: 35px;
    }
    .top-bar-container {
      padding: 0 8px;
    }
    .profile-picture {
      width: 18px;
      height: 18px;
    }
    .profile-text {
      font-size: 0.7rem;
    }
    .announcement-wrapper {
      max-width: 180px; /* Reduced to prevent overlap */
    }
    .announcement-image {
      width: 16px;
      height: 16px;
    }
    .announcement-text {
      font-size: 0.7rem;
    }
    .dropdown {
      width: 150px;
      right: 4px;
    }
  }
  /* Desktop PC adjustments */
  @media (min-width: 1024px) {
    .dropdown {
      left: 50%;
      right: auto;
      transform: translateX(-50%) !important;
      width: 200px;
    }
  }
  body.mobile-menu-open .top-bar {
    display: none;
  }
`;

// Inject styles into the document head
const styleSheet = document.createElement('style');
styleSheet.type = 'text/css';
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default TopBar;